import { isNativeApp } from '../platform.js';
import { db } from '../firebase-config.js';
import { doc, updateDoc } from 'firebase/firestore';

// Razorpay key (Web checkout only — the APK never shows a purchase flow, see
// purchasePlan below). Replace with your live key before enabling payments.
const RAZORPAY_KEY_ID = 'rzp_test_YOUR_KEY_HERE';

export function isBillingConfigured() {
  return !isNativeApp && !RAZORPAY_KEY_ID.includes('YOUR_KEY_HERE');
}

/**
 * Trigger purchase flow
 * @param {Object} plan - { id, name, price, days }
 * @param {Object} user - { uid, email, phoneNumber }
 */
export async function purchasePlan(plan, user) {
  if (!user || !user.uid) {
    throw new Error('You must be logged in to upgrade.');
  }

  // Google Play policy: digital goods inside the APK MUST use Google Play
  // Billing. Until that is integrated (paid dev work), the native app shows
  // NO purchase flow at all — a fake/mock flow here would get the app
  // removed from the Play Store.
  if (isNativeApp) {
    throw new Error('In-app purchase Android app me abhi available nahi hai. PRO activate karne ke liye admin se contact karo.');
  }
  if (!isBillingConfigured()) {
    throw new Error('Payments abhi enable nahi hue hain. PRO ke liye admin se contact karo.');
  }

  const result = await purchaseViaRazorpay(plan, user);

  if (result && result.success) {
    // Client-side upgrade (Low Security / 100% Free architecture without
    // webhooks — a deliberate tradeoff to stay on Firebase's free plan).
    // The payment reference is stored so upgrades can be audited manually
    // against the Razorpay dashboard, and security rules cap the expiry.
    const expiryDate = new Date();
    expiryDate.setDate(expiryDate.getDate() + plan.days);

    try {
      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, {
        plan: 'pro',
        planExpiry: expiryDate.getTime(),
        planStartedAt: Date.now(),
        planId: plan.id,
        planPaymentRef: result.ref || ''
      });
    } catch (err) {
      console.error('Failed to upgrade plan in Firestore:', err);
      throw new Error('Payment succeeded but profile update failed. Please contact admin with your payment ID: ' + (result.ref || ''));
    }
  }

  return result;
}

async function purchaseViaRazorpay(plan, user) {
  return new Promise((resolve, reject) => {
    if (!window.Razorpay) {
      reject(new Error('Payment gateway not loaded. Please try again.'));
      return;
    }

    const options = {
      key: RAZORPAY_KEY_ID,
      amount: plan.price * 100, // paisa
      currency: 'INR',
      name: 'FFPL Cricket',
      description: plan.name,
      handler: function (response) {
        // The server webhook handles the actual upgrade.
        resolve({ success: true, ref: response.razorpay_payment_id });
      },
      prefill: {
        email: user.email || '',
        contact: user.phoneNumber || ''
      },
      notes: {
        uid: user.uid,
        planId: plan.id,
        days: plan.days
      },
      theme: { color: '#fbbf24' }
    };

    try {
      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', function (response) {
        reject(new Error(response.error.description || 'Payment failed'));
      });
      rzp.open();
    } catch (err) {
      reject(err);
    }
  });
}

export function loadBillingScript() {
  if (isNativeApp || document.getElementById('rzp-script')) return;
  const script = document.createElement('script');
  script.id = 'rzp-script';
  script.src = 'https://checkout.razorpay.com/v1/checkout.js';
  document.head.appendChild(script);
}
